import React, { useState } from "react";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import {
  EllipsisHorizontalIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Button } from "components/ui";
import { Td } from "components/ui/Table";
import { downloadLeadDocument, deleteLeadDocument } from "services/leadsService";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { ViewDocumentModal } from "./ViewDocumentModal";
import { toast } from "sonner";
import { hasPermission } from "utils/permissionsUtils";
import { getDocumentUrl } from "../utils/documentUtils";
export function DocumentActions({
  document,
  refetch,
  useCase = "card",
  refreshProgressJourney = () => { },
  onVerificationAction,
  IsFinalApproved = false,
  onView,
  isStudent = false,
}) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const hasVerificationAccess = document?.canApproveOrReject;
  const handleViewDocument = (e) => {
    if (e) e.stopPropagation();
    onView?.();
  };

  const handleModalDownload = async (doc) => {
    if (doc.id) {
      try {
        const result = await downloadLeadDocument(doc.id, doc.name);
        if (result?.success) toast.success("Document downloaded successfully.");
      } catch (error) {
        toast.error("Error downloading document or document not found.");
      }
    }
  };

  const handleModalOpenInNewTab = (doc) => {
    const filePath = doc?.filePath;
    window.open(getDocumentUrl(filePath), "_blank");
  };

  const handleOpenInNewTab = () => {
    const filePath = document?.filePath
    window.open(getDocumentUrl(filePath), "_blank")

  };


  const handleDownloadDocument = async () => {
    if (document.id) {
      try {
        const result = await downloadLeadDocument(document.id, document.name);
        if (result?.success) toast.success("Document downloaded successfully.");
      } catch (error) {
        toast.error("Error downloading document or document not found.");
      }
    }
  };

  const handleDeleteDocument = () => {

    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (document.id) {
      try {
        const result = await deleteLeadDocument(document.id);
        if (result?.success) {
          toast.success("Document deleted successfully.");
          setDeleteModalOpen(false);
          await refreshProgressJourney();
          refetch();
        } else {
          toast.error(result?.error || "Failed to delete document.");
        }
      } catch (error) {
        toast.error("Error deleting document.");
        console.error("Delete document error:", error);
      }
    }
  };

  const showApproveOption = document.verificationStatus === 0 || document.verificationStatus === 2;
  const showRejectOption = document.verificationStatus === 0 || document.verificationStatus === 1;

  const handleVerificationAction = (event, action) => {
    event.stopPropagation();
    if (typeof onVerificationAction === "function") {
      onVerificationAction(document, action);
    }
  };

  const renderMenuItems = () => (
    <>
      <MenuItem>
        {({ focus }) => (
          <button
            onClick={handleViewDocument}
            className={clsx(
              "flex h-9 w-full items-center cursor-pointer space-x-3 px-3 tracking-wide outline-hidden transition-colors",
              focus &&
              "dark:bg-dark-600 dark:text-dark-100 bg-gray-100 text-gray-800",
            )}
          >
            <EyeIcon className="size-3.5 stroke-1" />
            <span className="truncate">View</span>
          </button>
        )}
      </MenuItem>
      <MenuItem>
        {({ focus }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenInNewTab();
            }}
            className={clsx(
              "flex h-9 w-full items-center cursor-pointer space-x-3 px-3 tracking-wide outline-hidden transition-colors",
              focus &&
              "dark:bg-dark-600 dark:text-dark-100 bg-gray-100 text-gray-800",
            )}
          >
            <ArrowTopRightOnSquareIcon className="size-3.5 stroke-1" />
            <span className="truncate">Open in New Tab</span>
          </button>
        )}
      </MenuItem>
      {showApproveOption && hasVerificationAccess && !IsFinalApproved && !isStudent ? (
        <MenuItem>
          {({ focus }) => (
            <button
              onClick={(e) => handleVerificationAction(e, "approve")}
              className={clsx(
                "flex h-9 w-full items-center cursor-pointer space-x-3 px-3 tracking-wide outline-hidden transition-colors",
                focus &&
                "dark:bg-dark-600 dark:text-dark-100 bg-gray-100 text-gray-800",
              )}
            >
              <CheckCircleIcon className="size-3.5 stroke-1 text-emerald-500" />
              <span className="truncate">Approve</span>
            </button>
          )}
        </MenuItem>
      ) : null}
      {showRejectOption && hasVerificationAccess && !IsFinalApproved && !isStudent ? (
        <MenuItem>
          {({ focus }) => (
            <button
              onClick={(e) => handleVerificationAction(e, "reject")}
              className={clsx(
                "flex h-9 w-full items-center cursor-pointer space-x-3 px-3 tracking-wide outline-hidden transition-colors",
                focus &&
                "dark:bg-dark-600 dark:text-dark-100 bg-gray-100 text-gray-800",
              )}
            >
              <XCircleIcon className="size-3.5 stroke-1 text-red-500" />
              <span className="truncate">Reject</span>
            </button>
          )}
        </MenuItem>
      ) : null}
      {hasPermission("MLLE") && (
        <MenuItem>
          {({ focus }) => (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadDocument();
              }}
              className={clsx(
                "flex h-9 w-full items-center cursor-pointer space-x-3 px-3 tracking-wide outline-hidden transition-colors",
                focus &&
                "dark:bg-dark-600 dark:text-dark-100 bg-gray-100 text-gray-800",
              )}
            >
              <ArrowDownTrayIcon className="size-3.5 stroke-1" />
              <span className="truncate">Download</span>
            </button>
          )}
        </MenuItem>
      )}
      {hasPermission("MLLE") && !IsFinalApproved && !isStudent && (
        <MenuItem>
          {({ focus }) => (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteDocument();
              }}
              className={clsx(
                "flex h-9 w-full items-center cursor-pointer space-x-3 px-3 tracking-wide text-red-600 outline-hidden transition-colors dark:text-red-400",
                focus && "bg-red-50 dark:bg-red-900/10",
              )}
            >
              <TrashIcon className="size-3.5 stroke-1" />
              <span className="truncate">Delete</span>
            </button>
          )}
        </MenuItem>
      )}
    </>
  );

  return (
    <>
      {useCase === "card" ? (
        <div className="flex justify-center">
          <Menu as="div" className="relative inline-block text-left">
            <MenuButton
              as="div"
              className="inline-flex h-4 w-4 items-center justify-center rounded-full
               bg-gray-100 text-gray-900 hover:bg-gray-200
               dark:bg-dark-600 dark:text-white dark:hover:bg-dark-500
               sm:h-7 sm:w-7"
              onClick={(e) => e.stopPropagation()}
            >
              <EllipsisHorizontalIcon className="h-5 w-5 stroke-[1.8]" />
            </MenuButton>

            <Transition
              as={Fragment}
              enter="transition ease-out"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <MenuItems
                anchor="bottom end"
                portal
                className="z-50 min-w-[10rem] rounded-lg border border-gray-300 bg-white py-1 shadow-lg shadow-gray-200/50 outline-hidden focus-visible:outline-hidden dark:bg-dark-750 dark:border-dark-500 dark:shadow-none"
              >
                {renderMenuItems()}
              </MenuItems>
            </Transition>
          </Menu>

        </div>
      ) : (
        <Td className="">
          <div className="flex justify-center">
            <Menu as="div" className="relative inline-block text-left">
              <MenuButton
                as={Button}
                variant="flat"
                isIcon
                className="size-6 rounded-full sm:size-7"
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisHorizontalIcon className="size-4" />
              </MenuButton>
              <Transition
                as={Fragment}
                enter="transition ease-out"
                enterFrom="opacity-0 translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-2"
              >
                <MenuItems
                  anchor="bottom end"
                  portal
                  className="dark:bg-dark-750 dark:border-dark-500 z-50 min-w-[10rem] rounded-lg border border-gray-300 bg-white py-1 shadow-lg shadow-gray-200/50 outline-hidden focus-visible:outline-hidden dark:shadow-none"
                >
                  {renderMenuItems()}
                </MenuItems>
              </Transition>
            </Menu>
          </div>
        </Td>
      )}
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onOk={handleConfirmDelete}
        state="pending"
        messages={{
          pending: {
            title: "Delete Document",
            description: `Are you sure you want to delete "${document.name}"? This action cannot be undone.`,
            actionText: "Delete",
          },
          success: {
            title: "Document Deleted",
            description: "The document has been successfully deleted.",
            actionText: "Done",
          },
          error: {
            title: "Delete Failed",
            description: "Failed to delete the document. Please try again.",
            actionText: "Retry",
          },
        }}
        buttonColor="error"
      />
    </>
  );
}

DocumentActions.propTypes = {
  document: PropTypes.object.isRequired,
  refetch: PropTypes.func,
  useCase: PropTypes.oneOf(["card", "table"]),
  refreshProgressJourney: PropTypes.func,
  onVerificationAction: PropTypes.func,
  onView: PropTypes.func,
};

DocumentActions.defaultProps = {
  refetch: () => { },
  useCase: "card",
  refreshProgressJourney: () => { },
  onVerificationAction: undefined,
};

export default DocumentActions;
